import { createSyncStoragePersister } from '@tanstack/query-sync-storage-persister';
import { QueryClient } from '@tanstack/react-query';
import { PersistQueryClientProvider } from '@tanstack/react-query-persist-client';
import { lazy, PropsWithChildren, Suspense } from 'react';
import AppProvider from './AppContextProvider';
import BotConfigProvider from './BotConfigProvider';
import SessionProvider from './SessionProvider';
import { ThemeProvider } from './ThemeProvider';

const ErrorBoundary = lazy(
  () => import('@/components/ErrorBoundary/ErrorBoundary'),
);

// eslint-disable-next-line react-refresh/only-export-components
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 30, // 30 seconds
      gcTime: 1000 * 60 * 30, // 30 minutes
      refetchOnWindowFocus: false,
    },
  },
});

const sessionStoragePersistor = createSyncStoragePersister({
  storage: window.sessionStorage,
});

const CombinedProvider = ({ children }: PropsWithChildren<unknown>) => {
  return (
    <PersistQueryClientProvider
      client={queryClient}
      persistOptions={{
        maxAge: 1000 * 60 * 30, // 30 minutes
        persister: sessionStoragePersistor,
        dehydrateOptions: {
          shouldDehydrateQuery: (query) => {
            return (
              query.queryKey.includes('persist') &&
              query.state.status !== 'pending'
            );
          },
          shouldDehydrateMutation: () => false,
        },
      }}
    >
      <AppProvider>
        <BotConfigProvider>
          <Suspense fallback={null}>
            <ErrorBoundary>
              <SessionProvider>
                <ThemeProvider>{children}</ThemeProvider>
              </SessionProvider>
            </ErrorBoundary>
          </Suspense>
        </BotConfigProvider>
      </AppProvider>
    </PersistQueryClientProvider>
  );
};

export default CombinedProvider;
